import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import ReplayOutlined from "@mui/icons-material/ReplayOutlined";
import { Link as RouterLink } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import ArchitectChatPanel from "../components/architect/ArchitectChatPanel";
import IntakeAssessmentForm from "../components/architect/IntakeAssessmentForm";
import IntakeEvaluationPanel from "../components/architect/IntakeEvaluationPanel";
import { useArchitectIntakeAgentChat } from "../hooks/useArchitectIntakeAgentChat";
import { apiService } from "../api/services/apiService";
import { getTenantId } from "../utils/jwtUtils";
import { EntityStatus } from "../types";
import type { ApiIntake } from "../types/apiIntake";
import { stripStructuredBlocksForDisplay } from "../utils/agentMessageDisplay";
import {
  buildIntakeAssessmentPrompt,
  buildIntakeFollowUpPrompt,
  extractIntakeEvaluationFromMessage,
  graphqlChangePath,
  graphqlCoverage,
  INTAKE_CHANGE_PATH_HELP,
  INTAKE_CHANGE_PATH_LABELS,
  intakeAssessmentDisplaySummary,
  parseIntakeEvaluationJson,
  type IntakeEvaluationPayload,
} from "../utils/intakeEvaluation";

type IntakePhase = "start" | "details" | "running" | "results" | "chat";

const STEPS = ["Describe", "Assess", "Review"];

function stepperIndex(phase: IntakePhase): number {
  if (phase === "details") return 0;
  if (phase === "running") return 1;
  if (phase === "results" || phase === "chat") return 2;
  return -1;
}

function evaluationFromIntake(intake: ApiIntake): IntakeEvaluationPayload | null {
  const fromJson = parseIntakeEvaluationJson(intake.evaluation);
  if (fromJson) return fromJson;
  if (!intake.coverage || intake.coveragePercent == null || !intake.recommendedChangePath) {
    return null;
  }
  return {
    satisfied: intake.coverage === "FULL",
    coverage: intake.coverage.toLowerCase() as IntakeEvaluationPayload["coverage"],
    coveragePercent: intake.coveragePercent,
    requirementSummary: intake.description ?? intake.name,
    capabilities: [],
    matchedApis: (intake.matchedApis ?? []).map((m) => ({
      uuid: m.api?.uuid ?? "",
      name: m.api?.name,
      version: m.api?.version,
      coveragePercent: m.coveragePercent ?? undefined,
      role: m.matchRole?.toLowerCase() as IntakeEvaluationPayload["matchedApis"][number]["role"],
      notes: m.notes ?? undefined,
    })),
    recommendedChangePath: intake.recommendedChangePath
      .toLowerCase()
      .replace(/_/g, "-") as IntakeEvaluationPayload["recommendedChangePath"],
    recommendedApiUuid: intake.recommendedApi?.uuid ?? null,
    rationale: intake.description ?? undefined,
  };
}

const ArchitectIntakeAssistantPage: React.FC = () => {
  const {
    messages,
    sendMessage,
    resetConversation,
    loading,
    error,
    sessionReady,
    contextUsage,
  } = useArchitectIntakeAgentChat();
  const [phase, setPhase] = useState<IntakePhase>("start");
  const [evaluation, setEvaluation] = useState<IntakeEvaluationPayload | null>(null);
  const [requirementText, setRequirementText] = useState("");
  const [intakeName, setIntakeName] = useState("");
  const [actors, setActors] = useState("");
  const [relatedDocsMarkdown, setRelatedDocsMarkdown] = useState<string | undefined>();
  const [pastIntakes, setPastIntakes] = useState<ApiIntake[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedUuid, setSavedUuid] = useState<string | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState("");

  const lastAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === "assistant"),
    [messages],
  );

  useEffect(() => {
    if (!lastAssistant?.content) return;
    const extracted = extractIntakeEvaluationFromMessage(lastAssistant.content);
    if (extracted) {
      setEvaluation(extracted);
      if (extracted.requirementSummary && !intakeName) {
        setIntakeName(extracted.requirementSummary.slice(0, 255));
      }
    }
  }, [lastAssistant?.content, intakeName]);

  const loadPast = async () => {
    try {
      setListError(null);
      const rows = await apiService.getApiIntakes();
      setPastIntakes(rows);
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : "Failed to load past intakes");
    }
  };

  useEffect(() => {
    void loadPast();
  }, []);

  const handleStart = () => {
    resetConversation();
    setEvaluation(null);
    setSavedUuid(null);
    setSelectedUuid(null);
    setSaveError(null);
    setRequirementText("");
    setIntakeName("");
    setActors("");
    setRelatedDocsMarkdown(undefined);
    setPhase("details");
  };

  const handleSelectPast = (intake: ApiIntake) => {
    resetConversation();
    setSelectedUuid(intake.uuid);
    setSavedUuid(intake.uuid);
    setIntakeName(intake.name);
    setRequirementText(intake.requirementText);
    setRelatedDocsMarkdown(intake.relatedDocsMarkdown ?? undefined);
    setActors("");
    setEvaluation(evaluationFromIntake(intake));
    setSaveError(null);
    setPhase("results");
  };

  const handleRunAssessment = async (docsMarkdown?: string) => {
    const requirement = requirementText.trim();
    if (!requirement) return;
    setEvaluation(null);
    setSavedUuid(null);
    setSelectedUuid(null);
    setSaveError(null);
    setRelatedDocsMarkdown(docsMarkdown);
    setPhase("running");
    const prompt = buildIntakeAssessmentPrompt({
      name: intakeName,
      actors,
      requirement,
      relatedDocs: docsMarkdown,
    });
    const response = await sendMessage(prompt, {
      displayContent: intakeAssessmentDisplaySummary({
        name: intakeName,
        actors,
        requirement,
      }),
    });
    if (!response) {
      setPhase("details");
      return;
    }
    setPhase("results");
  };

  const handleOpenChat = () => {
    setPhase("chat");
    if (messages.length > 0 || !evaluation) return;
    void sendMessage(
      buildIntakeFollowUpPrompt({
        name: intakeName,
        requirement: requirementText,
        evaluation,
      }),
      {
        displayContent: `Discussing saved assessment${intakeName ? `: ${intakeName}` : ""}`,
      },
    );
  };

  const handleSave = async () => {
    if (!evaluation) return;
    const tenantId = getTenantId();
    if (!tenantId) {
      setSaveError("Tenant is required to save");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const name = intakeName.trim() || evaluation.requirementSummary || "Untitled intake";
      const input = {
        tenantId,
        name,
        description: evaluation.requirementSummary,
        status: EntityStatus.DRAFT,
        requirementText: requirementText.trim() || evaluation.requirementSummary || name,
        relatedDocsMarkdown,
        evaluation: JSON.stringify(evaluation),
        coverage: graphqlCoverage(evaluation.coverage),
        coveragePercent: evaluation.coveragePercent,
        recommendedChangePath: graphqlChangePath(evaluation.recommendedChangePath),
        recommendedApiUuid: evaluation.recommendedApiUuid || undefined,
        matchedApis: evaluation.matchedApis
          .filter((m) => m.uuid)
          .map((m) => ({
            apiUuid: m.uuid,
            coveragePercent: m.coveragePercent,
            matchRole: (m.role ?? "supporting").toUpperCase() as "PRIMARY" | "SUPPORTING",
            notes: m.notes,
          })),
      };
      const saved = savedUuid
        ? await apiService.updateApiIntake(savedUuid, input)
        : await apiService.createApiIntake(input);
      setSavedUuid(saved.uuid);
      setSelectedUuid(saved.uuid);
      await loadPast();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Failed to save intake");
    } finally {
      setSaving(false);
    }
  };

  const filteredIntakes = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return pastIntakes;
    return pastIntakes.filter((intake) => {
      const hay = [
        intake.name,
        intake.coverage ?? "",
        intake.coveragePercent != null ? String(intake.coveragePercent) : "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [pastIntakes, listFilter]);

  const assistantNarrative = lastAssistant?.content
    ? stripStructuredBlocksForDisplay(lastAssistant.content, "architect")
    : "";

  const streamingPreview = lastAssistant?.content
    ? stripStructuredBlocksForDisplay(lastAssistant.content, "architect")
    : "";

  const pastList = (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
        Past assessments
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Filter by name, coverage, %"
        value={listFilter}
        onChange={(e) => setListFilter(e.target.value)}
        sx={{ mb: 1 }}
      />
      {listError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {listError}
        </Alert>
      )}
      <List dense disablePadding>
        {filteredIntakes.map((intake) => (
          <ListItemButton
            key={intake.uuid}
            selected={selectedUuid === intake.uuid}
            onClick={() => handleSelectPast(intake)}
          >
            <ListItemText
              primary={intake.name}
              secondary={
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                  {intake.coverage && (
                    <Chip size="small" label={intake.coverage.toLowerCase()} />
                  )}
                  {intake.coveragePercent != null && (
                    <Typography variant="caption">{intake.coveragePercent}%</Typography>
                  )}
                  {intake.createdOn && (
                    <Typography variant="caption" color="text.secondary">
                      {intake.createdOn.slice(0, 10)}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </ListItemButton>
        ))}
        {filteredIntakes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            {pastIntakes.length === 0
              ? "No saved assessments yet. Start one to score a requirement against the catalog."
              : "No matching assessments."}
          </Typography>
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Intake Assistant"
        pageDescription="Start an assessment, review catalog coverage, then chat with the assistant if you need to go deeper."
      />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Button
          component={RouterLink}
          to="/agent-dashboard/architect"
          sx={{ mb: 2, textTransform: "none" }}
        >
          Back to Architect Agent
        </Button>

        {phase !== "start" && (
          <Stepper activeStep={Math.max(0, stepperIndex(phase))} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {phase === "start" && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 4 }}>
                <Stack spacing={2}>
                  <Typography variant="h5">Start an assessment</Typography>
                  <Typography color="text.secondary">
                    You do not need to guess existing API names. Describe the need; we score it
                    against this tenant catalog and show what to do next.
                  </Typography>
                  <Stack spacing={1.5} sx={{ py: 1 }}>
                    <Typography variant="body2">
                      1. <strong>Describe</strong> who needs it and what they must be able to do.
                    </Typography>
                    <Typography variant="body2">
                      2. <strong>Assess</strong> — a one-shot catalog check (no back-and-forth
                      yet). You get coverage %, gaps, and a recommended path.
                    </Typography>
                    <Typography variant="body2">
                      3. <strong>Review</strong> the result, save it, then optionally open a chat
                      to ask follow-up questions.
                    </Typography>
                  </Stack>
                  <Box>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<AssignmentOutlined />}
                      onClick={handleStart}
                      sx={{ textTransform: "none" }}
                    >
                      Start an assessment
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              {pastList}
            </Grid>
          </Grid>
        )}

        {phase === "details" && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <IntakeAssessmentForm
                name={intakeName}
                actors={actors}
                requirement={requirementText}
                submitting={loading}
                sessionReady={sessionReady}
                error={error}
                onNameChange={setIntakeName}
                onActorsChange={setActors}
                onRequirementChange={setRequirementText}
                onCancel={() => setPhase("start")}
                onSubmit={(docs) => void handleRunAssessment(docs)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {pastList}
            </Grid>
          </Grid>
        )}

        {phase === "running" && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Assessing catalog coverage</Typography>
              <Typography variant="body2" color="text.secondary">
                Searching existing APIs and scoring the capabilities in your intake. Chat is
                available after this result is ready — you do not need to type anything now.
              </Typography>
              <LinearProgress />
              {intakeName && (
                <Typography variant="body2">
                  <strong>Intake:</strong> {intakeName}
                </Typography>
              )}
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {requirementText}
              </Typography>
              {streamingPreview && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Assistant is working…
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {streamingPreview}
                  </Typography>
                </Paper>
              )}
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </Paper>
        )}

        {(phase === "results" || phase === "chat") && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={phase === "chat" ? 5 : 7}>
              {phase === "chat" ? (
                <ArchitectChatPanel
                  messages={messages}
                  onSend={sendMessage}
                  loading={loading}
                  error={error}
                  contextUsage={contextUsage}
                  enableFileAttach
                  title="Follow-up with the assistant"
                  placeholder="Ask about gaps, a matched API, or the recommended path…"
                  thinkingLabel="Intake assistant is working…"
                  quickPrompts={[
                    "Explain why this path is better than a new API",
                    "Which capabilities are still missing?",
                  ]}
                />
              ) : (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      What you asked
                    </Typography>
                    {intakeName && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{intakeName}</strong>
                      </Typography>
                    )}
                    {actors && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Who: {actors}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {requirementText}
                    </Typography>
                  </Paper>
                  {evaluation && (
                    <Alert
                      severity={evaluation.satisfied ? "success" : "warning"}
                      sx={{ alignItems: "flex-start" }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Recommended next step:{" "}
                        {INTAKE_CHANGE_PATH_LABELS[evaluation.recommendedChangePath]}
                      </Typography>
                      <Typography variant="body2">
                        {INTAKE_CHANGE_PATH_HELP[evaluation.recommendedChangePath]} Catalog
                        coverage is {evaluation.coveragePercent}% (
                        {evaluation.coverage}).
                      </Typography>
                    </Alert>
                  )}
                  {assistantNarrative && (
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Assistant summary
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {assistantNarrative}
                      </Typography>
                    </Paper>
                  )}
                  {!evaluation && (
                    <Alert severity="info">
                      The assistant did not return a structured evaluation. Open a follow-up
                      chat to refine the requirement, or start again with more specific
                      operations (resource + verb).
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      startIcon={<ChatOutlined />}
                      onClick={handleOpenChat}
                      sx={{ textTransform: "none" }}
                    >
                      Talk to the assistant
                    </Button>
                    <Button
                      startIcon={<ReplayOutlined />}
                      onClick={handleStart}
                      sx={{ textTransform: "none" }}
                    >
                      Start another assessment
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} md={phase === "chat" ? 4 : 5}>
              {phase === "chat" && (
                <Button onClick={() => setPhase("results")} sx={{ mb: 1, textTransform: "none" }}>
                  Back to results
                </Button>
              )}
              <TextField
                fullWidth
                size="small"
                label="Intake name"
                value={intakeName}
                onChange={(e) => setIntakeName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <IntakeEvaluationPanel
                evaluation={evaluation}
                saving={saving}
                saveError={saveError}
                savedUuid={savedUuid}
                onSave={evaluation ? handleSave : undefined}
              />
              {phase === "results" && <Box sx={{ mt: 2 }}>{pastList}</Box>}
            </Grid>
            {phase === "chat" && (
              <Grid item xs={12} md={3}>
                {pastList}
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default ArchitectIntakeAssistantPage;
